import { useState, useRef, useEffect, useId, useCallback } from 'react';
import { useEstimates } from '../../context/EstimateContext';
import { useInvoices } from '../../context/InvoiceContext';
import Button from '../ui/Button';

const ACCENTS = {
  blue:    { iconBg: 'bg-blue-50 dark:bg-blue-900/20',     iconColor: 'text-blue-500 dark:text-blue-400',     hex: '#3B82F6', bar: 'bg-blue-400/60' },
  emerald: { iconBg: 'bg-emerald-50 dark:bg-emerald-900/20', iconColor: 'text-emerald-600 dark:text-emerald-400', hex: '#10B981', bar: 'bg-emerald-400/60' },
  amber:   { iconBg: 'bg-amber-50 dark:bg-amber-900/20',   iconColor: 'text-amber-600 dark:text-amber-400',   hex: '#F59E0B', bar: 'bg-amber-400/60' },
  violet:  { iconBg: 'bg-violet-50 dark:bg-violet-900/20', iconColor: 'text-violet-600 dark:text-violet-400', hex: '#8B5CF6', bar: 'bg-violet-400/60' },
  sky:     { iconBg: 'bg-sky-50 dark:bg-sky-900/20',       iconColor: 'text-sky-600 dark:text-sky-400',       hex: '#0EA5E9', bar: 'bg-sky-400/60' },
  green:   { iconBg: 'bg-green-50 dark:bg-green-900/20',   iconColor: 'text-green-600 dark:text-green-400',   hex: '#22C55E', bar: 'bg-green-400/60' },
};

// Parse numeric value from display string like "61%", "$5,840", "1.8d"
function parseKpiValue(raw) {
  const s = String(raw).replace(/[$,%d\s]/g, '').replace(/,/g, '');
  return parseFloat(s);
}

// Detect prefix, suffix, decimal places, and whether to use k-abbreviation
function parseFormat(raw) {
  const s = String(raw).trim();
  const prefix = s.startsWith('$') ? '$' : '';
  const noPrefix = s.slice(prefix.length);
  const isK = noPrefix.endsWith('k');
  const suffix = isK ? 'k' : noPrefix.endsWith('%') ? '%' : noPrefix.endsWith('d') ? 'd' : '';
  const numStr = noPrefix.slice(0, noPrefix.length - suffix.length).replace(/,/g, '');
  const decimals = numStr.includes('.') ? numStr.split('.')[1].length : 0;
  const hasCommas = raw.includes(',') && !isK;
  return { prefix, suffix, decimals, hasCommas, isK };
}

function applyFormat(value, fmt) {
  if (fmt.isK) {
    const n = value / 1000;
    return `${fmt.prefix}${n.toFixed(fmt.decimals || 1)}k`;
  }
  if (fmt.decimals > 0) return `${fmt.prefix}${value.toFixed(fmt.decimals)}${fmt.suffix}`;
  const rounded = Math.round(value);
  if (fmt.hasCommas) return `${fmt.prefix}${rounded.toLocaleString('en-US')}${fmt.suffix}`;
  return `${fmt.prefix}${rounded}${fmt.suffix}`;
}

// Easing: ease-out cubic
function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

function useCountUp(targetRaw, duration = 700) {
  const fmt = parseFormat(targetRaw);
  const targetNum = parseKpiValue(targetRaw);
  const [display, setDisplay] = useState(targetRaw);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (isNaN(targetNum) || targetNum === 0) {
      setDisplay(targetRaw);
      return;
    }
    // Reset to zero before counting up
    setDisplay(applyFormat(0, fmt));
    startRef.current = null;

    const tick = (now) => {
      if (!startRef.current) startRef.current = now;
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      setDisplay(applyFormat(targetNum * eased, fmt));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRaw]);

  return display;
}

// Default thresholds — Approval Rate is pre-set below threshold (61% < 65%) to demo the alert
const DEFAULT_THRESHOLDS = {
  'Approval Rate': { alertBelow: 65, alertAbove: null },
};

function ThresholdPopover({ kpiLabel, _numericValue, onClose }) {
  const [thresholds, setThresholds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('wm-kpi-thresholds') || '{}');
      return saved[kpiLabel] || DEFAULT_THRESHOLDS[kpiLabel] || { alertBelow: '', alertAbove: '' };
    } catch { return { alertBelow: '', alertAbove: '' }; }
  });

  const save = () => {
    try {
      const all = JSON.parse(localStorage.getItem('wm-kpi-thresholds') || '{}');
      all[kpiLabel] = {
        alertBelow: thresholds.alertBelow !== '' ? Number(thresholds.alertBelow) : null,
        alertAbove: thresholds.alertAbove !== '' ? Number(thresholds.alertAbove) : null,
      };
      localStorage.setItem('wm-kpi-thresholds', JSON.stringify(all));
    } catch { /* ignore */ }
    onClose();
  };

  return (
    <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded shadow-lg p-3 w-48">
      <p className="text-[var(--wm-text-caption)] font-medium uppercase tracking-wide text-[#64748B] dark:text-[#7D93AE] mb-2">Alert Thresholds</p>
      <div className="space-y-2">
        <div>
          <label className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] block mb-0.5">Alert if below</label>
          <input
            type="number"
            value={thresholds.alertBelow ?? ''}
            onChange={e => setThresholds(t => ({ ...t, alertBelow: e.target.value }))}
            placeholder="e.g. 65"
            className="w-full h-6 px-2 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-caption)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0]"
          />
        </div>
        <div>
          <label className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] block mb-0.5">Alert if above</label>
          <input
            type="number"
            value={thresholds.alertAbove ?? ''}
            onChange={e => setThresholds(t => ({ ...t, alertAbove: e.target.value }))}
            placeholder="e.g. 100"
            className="w-full h-6 px-2 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-caption)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0]"
          />
        </div>
      </div>
      <div className="flex gap-1.5 mt-3">
        <Button variant="primary" size="sm" className="flex-1" onClick={save}>Save</Button>
        <button onClick={onClose} className="flex-1 h-6 rounded border border-gray-200 dark:border-[#243348] text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors">Cancel</button>
      </div>
    </div>
  );
}

function getThreshold(label) {
  try {
    const all = JSON.parse(localStorage.getItem('wm-kpi-thresholds') || '{}');
    return all[label] || DEFAULT_THRESHOLDS[label] || null;
  } catch { return null; }
}

function getBreachStatus(numericValue, threshold) {
  if (!threshold) return null;
  if (threshold.alertBelow !== null && threshold.alertBelow !== '' && numericValue < Number(threshold.alertBelow)) return 'below';
  if (threshold.alertAbove !== null && threshold.alertAbove !== '' && numericValue > Number(threshold.alertAbove)) return 'above';
  return null;
}

// Returns 'surge' | 'drop' | null based on delta magnitude (threshold: ±15%)
// Skips tiles that already have a threshold-breach visual (those are already prominent)
function getKpiSignal(kpi, breach) {
  if (breach) return null;
  if (kpi.positive === null) return null;
  const mag = parseFloat(String(kpi.delta).replace(/[^0-9.]/g, ''));
  if (isNaN(mag) || mag < 15) return null;
  return kpi.positive ? 'surge' : 'drop';
}

function buildCountSparkline(items, dateField, days = 30) {
  const BUCKETS = 7;
  const now = Date.now();
  const windowMs = days * 86400000;
  const bucketMs = windowMs / BUCKETS;
  const counts = Array(BUCKETS).fill(0);
  items.forEach(item => {
    const val = item[dateField];
    if (!val) return;
    const ts = new Date(val).getTime();
    const age = now - ts;
    if (age >= 0 && age < windowMs) {
      const bucket = Math.min(Math.floor(age / bucketMs), BUCKETS - 1);
      counts[BUCKETS - 1 - bucket]++;
    }
  });
  const hasVariation = counts.some(v => v !== counts[0]);
  if (!hasVariation) return counts.map((v, i) => v + (i % 3 === 0 ? 1 : 0));
  return counts;
}

function buildValueSparkline(items, dateField, valueField, days = 30) {
  const BUCKETS = 7;
  const now = Date.now();
  const windowMs = days * 86400000;
  const bucketMs = windowMs / BUCKETS;
  const sums = Array(BUCKETS).fill(0);
  items.forEach(item => {
    const val = item[dateField];
    if (!val) return;
    const ts = new Date(val).getTime();
    const age = now - ts;
    if (age >= 0 && age < windowMs) {
      const bucket = Math.min(Math.floor(age / bucketMs), BUCKETS - 1);
      sums[BUCKETS - 1 - bucket] += item[valueField] || 0;
    }
  });
  const hasVariation = sums.some(v => v !== sums[0]);
  if (!hasVariation) return sums.map((v, i) => v + (i % 3 === 0 ? 100 : 0));
  return sums;
}

function SvgSparkline({ data, color }) {
  if (!data || data.length < 2) return <div className="h-8" />;
  const W = 100;
  const H = 32;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [
    parseFloat(((i / (data.length - 1)) * W).toFixed(2)),
    parseFloat((H - ((v - min) / range) * (H - 6) - 3).toFixed(2)),
  ]);
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
  const fillPath = `${linePath} L${W},${H} L0,${H} Z`;
  const uid = useId();
  const gradId = `spark-${uid.replace(/:/g, '')}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-8" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill={color} />
    </svg>
  );
}

function KpiTile({ kpi, accent }) {
  const [showPopover, setShowPopover] = useState(false);
  const [, forceUpdate] = useState(0);
  const ref = useRef(null);
  const animatedValue = useCountUp(kpi.value);

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const isTilted = tilt.x !== 0 || tilt.y !== 0;

  const handleMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    const nx = (e.clientX - left) / width - 0.5;   // -0.5 … +0.5
    const ny = (e.clientY - top)  / height - 0.5;
    setTilt({ x: ny * -10, y: nx * 10 });           // max ±10° tilt
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
  }, []);

  const isPositive = kpi.positive === true;
  const isNeutral  = kpi.positive === null;
  const deltaArrow = isNeutral ? null : kpi.delta.startsWith('-') ? '↓' : '↑';
  const deltaText  = isNeutral ? '—' : kpi.delta.replace('-', '').replace('+', '');
  const deltaBg    = isNeutral
    ? 'bg-gray-100 dark:bg-[#243348] text-[#64748B] dark:text-[#7D93AE]'
    : isPositive
    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
    : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400';

  const numericValue = parseKpiValue(kpi.value);
  const threshold    = getThreshold(kpi.label);
  const breach       = getBreachStatus(numericValue, threshold);
  const signal       = getKpiSignal(kpi, breach);

  const tileBg = breach === 'below'
    ? 'bg-red-50 dark:bg-red-900/10'
    : breach === 'above'
    ? 'bg-green-50 dark:bg-green-900/10'
    : 'bg-white dark:bg-[#1B2A3E]';

  useEffect(() => {
    if (!showPopover) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setShowPopover(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showPopover]);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(400px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${isTilted ? 1.02 : 1})`,
        boxShadow: isTilted
          ? '0 20px 48px rgba(0,0,0,0.22), 0 6px 16px rgba(0,0,0,0.12)'
          : '0 1px 3px rgba(0,0,0,0.07)',
        transition: !isTilted
          ? 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          : 'transform 0.06s ease-out, box-shadow 0.06s ease-out',
        willChange: 'transform',
      }}
      className={`${tileBg} ${signal === 'surge' ? 'kpi-surge' : signal === 'drop' ? 'kpi-drop' : ''} border border-gray-200 dark:border-[#243348] rounded-xl flex flex-col overflow-hidden relative`}
    >
      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Top row: icon + controls */}
        <div className="flex items-start justify-between">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconBg} ${accent.iconColor}`}>
            {kpi.icon}
          </div>
          <div className="flex items-center gap-1">
            {breach && (
              <span className={`text-[var(--wm-text-caption)] font-bold px-1 py-0.5 rounded ${
                breach === 'below'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {breach === 'below' ? '⚠ Low' : '↑ High'}
              </span>
            )}
            <span className={`text-[var(--wm-text-meta)] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${deltaBg}`}>
              {deltaArrow && <span>{deltaArrow}</span>}
              <span>{deltaText}</span>
            </span>
            <button
              onClick={() => setShowPopover(o => !o)}
              className="h-5 w-5 flex items-center justify-center rounded text-gray-300 dark:text-[#364860] hover:text-gray-400 dark:hover:text-[#7D93AE] transition-colors"
              title="Set alert thresholds"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Value block */}
        <div className="flex flex-col gap-0.5">
          <div className="font-bold tracking-tight leading-none text-[#0F1923] dark:text-[#F8FAFE] font-mono" style={{ fontSize: '1.571rem' }}>
            {animatedValue}
          </div>
          <div className="text-[var(--wm-text-caption)] font-medium uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] leading-tight">
            {kpi.label}
          </div>
          {kpi.secondary && (
            <div className="text-[var(--wm-text-caption)] text-gray-400 dark:text-[#4A6080] leading-tight mt-0.5">
              {kpi.secondary}
            </div>
          )}
        </div>

        {/* SVG Sparkline */}
        <SvgSparkline data={kpi.sparkData} color={accent.hex} />
      </div>

      {/* Accent bottom bar */}
      <div className={`h-[3px] flex-shrink-0 ${accent.bar}`} />

      {showPopover && (
        <ThresholdPopover
          kpiLabel={kpi.label}
          numericValue={numericValue}
          onClose={() => { setShowPopover(false); forceUpdate(n => n + 1); }}
        />
      )}
    </div>
  );
}

export default function KpiStrip() {
  const { estimates } = useEstimates();
  const { invoices } = useInvoices();

  // Estimates Written (total non-archived)
  const totalEstimates = estimates.filter(e => e.status !== 'archived').length;

  // Approval Rate (approved / (approved + declined + converted))
  const closedEstimates = estimates.filter(e => ['approved', 'converted', 'declined'].includes(e.status));
  const approvedEstimates = estimates.filter(e => ['approved', 'converted'].includes(e.status));
  const approvalRate = closedEstimates.length > 0
    ? Math.round((approvedEstimates.length / closedEstimates.length) * 100)
    : 0;

  // Avg Ticket (avg total of non-archived, non-declined estimates)
  const activeEsts = estimates.filter(e => !['archived', 'declined'].includes(e.status));
  const avgTicket = activeEsts.length > 0
    ? Math.round(activeEsts.reduce((s, e) => s + (e.total || 0), 0) / activeEsts.length)
    : 0;

  // Pipeline Value (sum of draft + sent + approved totals)
  const pipelineValue = estimates
    .filter(e => ['draft', 'sent', 'approved'].includes(e.status))
    .reduce((s, e) => s + (e.total || 0), 0);

  // Avg Time-to-Approval (avg days from createdAt to approvedAt, for approved estimates)
  const approvedWithDates = estimates.filter(e => e.approvedAt && e.createdAt);
  const avgApprovalDays = approvedWithDates.length > 0
    ? (approvedWithDates.reduce((s, e) => {
        const diff = new Date(e.approvedAt) - new Date(e.createdAt);
        return s + diff / 86400000;
      }, 0) / approvedWithDates.length).toFixed(1)
    : '—';

  // Revenue Booked (sum of paid invoices)
  const revenueBooked = invoices
    .filter(i => i.status === 'paid')
    .reduce((s, i) => s + (i.total || 0), 0);

  const fmt$ = (n) => n >= 1000
    ? `$${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k`
    : `$${Math.round(n).toLocaleString()}`;

  const sparkEstimatesWritten = buildCountSparkline(estimates, 'createdAt');
  const sparkApprovalRate     = buildCountSparkline(
    estimates.filter(e => ['approved', 'converted'].includes(e.status)), 'approvedAt'
  );
  const sparkAvgTicket        = buildValueSparkline(activeEsts, 'createdAt', 'total');
  const sparkPipeline         = buildValueSparkline(
    estimates.filter(e => ['draft', 'sent', 'approved'].includes(e.status)), 'createdAt', 'total'
  );
  const sparkTimeToApproval   = buildCountSparkline(approvedWithDates, 'approvedAt');
  const sparkRevenueBooked    = buildValueSparkline(
    invoices.filter(i => i.status === 'paid'), 'paidAt', 'total'
  );

  const paidInvoiceCount = invoices.filter(i => i.status === 'paid').length;

  const KPIS = [
    {
      label: 'Estimates Written',
      value: String(totalEstimates),
      delta: '+12%',
      positive: true,
      secondary: activeEsts.length === 0
        ? 'No estimates yet'
        : `${activeEsts.length} active · ${estimates.filter(e => e.status === 'sent').length} sent`,
      sparkData: sparkEstimatesWritten,
      accent: ACCENTS.blue,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
        </svg>
      ),
    },
    {
      label: 'Approval Rate',
      value: `${approvalRate}%`,
      delta: approvalRate >= 60 ? '+4%' : '-3%',
      positive: approvalRate >= 60,
      secondary: closedEstimates.length === 0
        ? 'No closed estimates yet'
        : `${approvedEstimates.length} of ${closedEstimates.length} closed`,
      sparkData: sparkApprovalRate,
      accent: ACCENTS.emerald,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: 'Avg Ticket',
      value: fmt$(avgTicket),
      delta: '+8%',
      positive: true,
      secondary: activeEsts.length === 0
        ? 'No estimates yet'
        : `across ${activeEsts.length} active estimate${activeEsts.length !== 1 ? 's' : ''}`,
      sparkData: sparkAvgTicket,
      accent: ACCENTS.amber,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75" />
        </svg>
      ),
    },
    {
      label: 'Pipeline Value',
      value: fmt$(pipelineValue),
      delta: '+5%',
      positive: true,
      secondary: (() => {
        const n = estimates.filter(e => ['draft','sent','approved'].includes(e.status)).length;
        return n === 0 ? 'No active pipeline' : `${n} in pipeline`;
      })(),
      sparkData: sparkPipeline,
      accent: ACCENTS.violet,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
    },
    {
      label: 'Avg Time-to-Approval',
      value: avgApprovalDays === '—' ? '—' : `${avgApprovalDays}d`,
      delta: '-22%',
      positive: true,
      lowerBetter: true,
      secondary: approvedWithDates.length === 0
        ? 'No approvals yet'
        : `${approvedWithDates.length} approved estimate${approvedWithDates.length !== 1 ? 's' : ''}`,
      sparkData: sparkTimeToApproval,
      accent: ACCENTS.sky,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: 'Revenue Booked',
      value: fmt$(revenueBooked),
      delta: '+17%',
      positive: true,
      secondary: (() => {
        const n = invoices.filter(i => i.status === 'paid').length;
        return n === 0 ? 'No paid invoices yet' : `${n} paid invoice${n !== 1 ? 's' : ''}`;
      })(),
      sparkData: sparkRevenueBooked,
      accent: ACCENTS.green,
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {KPIS.map((kpi) => (
        <KpiTile key={kpi.label} kpi={kpi} accent={kpi.accent} />
      ))}
    </div>
  );
}
